import { computed, ref } from 'vue'
import {
  useQuery,
  useMutation,
  provideApolloClient,
} from '@vue/apollo-composable'
import { isNil } from 'lodash-es'
import type { Get } from 'type-fest'

import { apolloClient } from '@/vue-apollo'
import { useXState } from '@/composition/useXState'
import { NotificationsState } from '@/store/notifications/NotificationsState'
import { parseServerDateString } from '@/utilities/date'
import { throwFriendlyError } from '@/functional/friendlyErrors'
import {
  NotificationsDocument,
  type NotificationsQuery,
  UpdateNotificationStateDocument,
  NotificationStatus,
  MarkAllNotificationsAsReadDocument,
  NotificationType,
} from '../../../generated/graphql'
import {
  type NotificationResourceRequestFeedbackStateUpdated,
  normalizeNotificationResourceRequestFeedbackStateUpdated,
  filterNotificationResourceRequestFeedbackStateUpdated,
  filterNotificationResourceRequestCommented,
  normalizeNotificationResourceRequestCommented,
  type NotificationResourceRequestCommented,
  type NotificationResourceRequestUpdated,
  normalizeNotificationResourceRequestUpdated,
} from '@/store/notifications/notificationTypes/resourceRequest'
import {
  normalizeNotificationLeaveRequestCreated,
  type NotificationLeaveRequestCreated,
  normalizeNotificationLeaveRequestUpdated,
  type NotificationLeaveRequestUpdated,
  normalizeNotificationLeaveRequestAccepted,
  type NotificationLeaveRequestAccepted,
  normalizeNotificationLeaveRequestDeclined,
  type NotificationLeaveRequestDeclined,
  type NotificationLeaveRequestCancelRequested,
  normalizeNotificationLeaveRequestCancelRequested,
  type NotificationLeaveRequestCancelDeclined,
  normalizeNotificationLeaveRequestCancelDeclined,
  type NotificationLeaveRequestCancelAccepted,
  normalizeNotificationLeaveRequestCancelAccepted,
  type NotificationLeaveRequestAborted,
  normalizeNotificationLeaveRequestAborted,
  type NotificationLeaveRequestAcceptedNotice,
  normalizeNotificationLeaveRequestAcceptedNotice,
  type NotificationLeaveRequestPartiallyAccepted,
  normalizeNotificationLeaveRequestPartiallyAccepted,
  type NotificationLeaveRequestDeclinedNotice,
  normalizeNotificationLeaveRequestDeclinedNotice,
  type NotificationLeaveRequestCancelAcceptedNotice,
  normalizeNotificationLeaveRequestCancelAcceptedNotice,
  type NotificationLeaveRequestCancelPartiallyAccepted,
  normalizeNotificationLeaveRequestCancelPartiallyAccepted,
  type NotificationLeaveRequestCancelDeclinedNotice,
  normalizeNotificationLeaveRequestCancelDeclinedNotice,
} from '@/store/notifications/notificationTypes/leaveRequest'
import {
  normalizeNotificationExternalServiceRequestCreated,
  type NotificationExternalServiceRequestCreated,
  normalizeNotificationExternalServiceRequestUpdated,
  type NotificationExternalServiceRequestUpdated,
  normalizeNotificationExternalServiceRequestAccepted,
  type NotificationExternalServiceRequestAccepted,
  normalizeNotificationExternalServiceRequestDeclined,
  type NotificationExternalServiceRequestDeclined,
  type NotificationExternalServiceRequestAcceptedNotice,
  normalizeNotificationExternalServiceRequestAcceptedNotice,
  type NotificationExternalServiceRequestDeclinedNotice,
  normalizeNotificationExternalServiceRequestDeclinedNotice,
} from '@/store/notifications/notificationTypes/externalServiceRequest'
import {
  type NotificationAddressUpdated,
  normalizeNotificationAddressUpdated,
} from '@/store/notifications/notificationTypes/address'
import { appState } from '@/state/index'
import { toArray } from '../../utilities/array'

export type NotificationFromQuery = NonNullable<
  Get<NotificationsQuery, 'notifications[0]'>
>

export type NotificationBase = {
  id: number
  isUnread: boolean
  date: Date
}

export interface NotificationError {
  type: 'NotificationError'
  props: {
    notificationId: number
    notificationType: string
    errorMessage?: string
  }
}

export type Notification = NotificationBase &
  (
    | NotificationError
    | NotificationResourceRequestFeedbackStateUpdated
    | NotificationResourceRequestCommented
    | NotificationLeaveRequestCreated
    | NotificationLeaveRequestUpdated
    | NotificationLeaveRequestAccepted
    | NotificationLeaveRequestDeclined
    | NotificationLeaveRequestCancelRequested
    | NotificationLeaveRequestCancelDeclined
    | NotificationLeaveRequestCancelAccepted
    | NotificationLeaveRequestAborted
    | NotificationLeaveRequestAcceptedNotice
    | NotificationLeaveRequestPartiallyAccepted
    | NotificationLeaveRequestDeclinedNotice
    | NotificationLeaveRequestCancelAcceptedNotice
    | NotificationLeaveRequestCancelPartiallyAccepted
    | NotificationLeaveRequestCancelDeclinedNotice
    | NotificationExternalServiceRequestCreated
    | NotificationExternalServiceRequestUpdated
    | NotificationExternalServiceRequestAccepted
    | NotificationExternalServiceRequestDeclined
    | NotificationExternalServiceRequestAcceptedNotice
    | NotificationExternalServiceRequestDeclinedNotice
    | NotificationResourceRequestUpdated
    | NotificationAddressUpdated
  )

const showRead = ref(false)

const notificationsQuery = provideApolloClient(apolloClient)(() =>
  useQuery(
    NotificationsDocument,
    () => ({
      includeRead: showRead.value,
    }),
    () => {
      const isEnabled = appState.state.value.matches('ready')

      return {
        enabled: isEnabled,
        notifyOnNetworkStatusChange: true,
        /**
         * When this writes to the cache it disturbs an open edit popover, probably
         * because some values are not requested or it does not write to the cache properly
         * @see https://gitlab.dev.innovation-agents.de/pro-musik/frontend/-/issues/1025
         */
        fetchPolicy: 'no-cache',
      }
    }
  )
)

const isLoading = computed(() => notificationsQuery.loading.value)

async function getNotifications() {
  await notificationsQuery.refetch()
}

const xstate = useXState(NotificationsState, {
  services: {
    getNotifications: getNotifications,
  },
})

/**
 * This should not be used directly, use notificationsFiltered instead!
 */
const notifications = computed(() => {
  return notificationsQuery.result.value?.notifications
})

/**
 * Filter out specific notifications which should not be displayed anywhere
 * This is for example the case for notifications which should be deleted but are not yet
 */
const notificationsFiltered = computed(() => {
  if (!notifications.value) return

  const result = notifications.value.filter((notification) => {
    if (!notification) return false

    if (
      notification.type === NotificationType.ResourceRequestFeedbackStateUpdated
    ) {
      return filterNotificationResourceRequestFeedbackStateUpdated(notification)
    }

    if (notification.type === NotificationType.ResourceRequestCommented) {
      return filterNotificationResourceRequestCommented(notification)
    }

    if (notification.type === NotificationType.ResourceRequestUpdated)
      return true
    if (notification.type === NotificationType.AddressUpdated) return true
    if (notification.type === NotificationType.LeaveRequestCreated) return true
    if (notification.type === NotificationType.LeaveRequestUpdated) return true
    if (notification.type === NotificationType.LeaveRequestAccepted) return true
    if (notification.type === NotificationType.LeaveRequestAcceptedNotice)
      return true
    if (notification.type === NotificationType.LeaveRequestPartiallyAccepted)
      return true
    if (notification.type === NotificationType.LeaveRequestDeclined) return true
    if (notification.type === NotificationType.LeaveRequestDeclinedNotice)
      return true
    if (notification.type === NotificationType.LeaveRequestCancelRequested)
      return true
    if (notification.type === NotificationType.LeaveRequestCancelAccepted)
      return true
    if (notification.type === NotificationType.LeaveRequestCancelAcceptedNotice)
      return true
    if (
      notification.type === NotificationType.LeaveRequestCancelPartiallyAccepted
    )
      return true
    if (notification.type === NotificationType.LeaveRequestCancelDeclined)
      return true
    if (notification.type === NotificationType.LeaveRequestCancelDeclinedNotice)
      return true
    if (notification.type === NotificationType.LeaveRequestAborted) return true
    if (notification.type === NotificationType.ExternalServiceRequestCreated)
      return true
    if (notification.type === NotificationType.ExternalServiceRequestUpdated)
      return true
    if (notification.type === NotificationType.ExternalServiceRequestAccepted)
      return true
    if (
      notification.type ===
      NotificationType.ExternalServiceRequestAcceptedNotice
    )
      return true
    if (notification.type === NotificationType.ExternalServiceRequestDeclined)
      return true
    if (
      notification.type ===
      NotificationType.ExternalServiceRequestDeclinedNotice
    )
      return true

    // const _exhaustiveCheck: never = notification.type
    console.log(`Notification with type "${notification.type}" not handled`)
  })

  return result
})

const hasUnreadNotifications = computed(() => {
  if (!notificationsFiltered.value) return false

  const unreadNotifications = notificationsFiltered.value.filter(
    (notification) => {
      if (!notification) return false
      if (notification.state === 'unread') return true

      return false
    }
  )

  return unreadNotifications.length > 0
})

/**
 * Save Read/Unread Status
 */
const updateNotificationStateMutation = provideApolloClient(apolloClient)(() =>
  useMutation(UpdateNotificationStateDocument, () => ({
    refetchQueries: ['Notifications'],
  }))
)

function markAsRead(ids: number | number[]) {
  const idsNormalized = toArray(ids)
  if (!idsNormalized?.length) return 0
  updateNotificationState(idsNormalized, NotificationStatus.read)
}

function markAsUnread(id: number) {
  // If notification is already unread, do nothing
  if (notificationsNormalized.value?.get(id)?.isUnread === true) {
    return
  }

  return updateNotificationState([id], NotificationStatus.unread)
}

async function updateNotificationState(
  ids: number[],
  status: NotificationStatus
) {
  try {
    await updateNotificationStateMutation.mutate({
      ids: ids,
      status: status,
    })
  } catch (error) {
    throwFriendlyError(error)
  }
}

/**
 * Mark all as read
 */
const markAllAsReadMutation = provideApolloClient(apolloClient)(() =>
  useMutation(MarkAllNotificationsAsReadDocument, () => ({
    refetchQueries: ['Notifications'],
  }))
)

async function markAllAsRead() {
  try {
    await markAllAsReadMutation.mutate()

    // Refetch query to reflect new state
    notificationsQuery.refetch()
  } catch (error) {
    throwFriendlyError(error)
  }
}

/**
 * Normalize Notifications
 */
const notificationsNormalized = computed(() => {
  if (!notificationsFiltered.value) return
  const notificationsNormalized = new Map<number, Notification>()

  notificationsFiltered.value.forEach((notification) => {
    if (!notification) return

    try {
      const notificationNormalized = normalizeNotification(notification)
      if (!notificationNormalized) return

      // Merge with base properties
      notificationsNormalized.set(notification.id, {
        ...normalizeNotificationBase(notification),
        ...notificationNormalized,
      })
    } catch (error) {
      console.error('Failed to normalize notification', {
        notificationId: notification.id,
        notificationType: notification.type,
        error,
      })

      const errorNotification: NotificationError = {
        type: 'NotificationError',
        props: {
          notificationId: notification.id,
          notificationType: notification.type,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      }

      notificationsNormalized.set(notification.id, {
        ...normalizeNotificationBase(notification),
        ...errorNotification,
      })
    }
  })

  return notificationsNormalized
})

function normalizeNotification(notification: NotificationFromQuery) {
  if (!notification) return

  if (
    notification.type === NotificationType.ResourceRequestFeedbackStateUpdated
  ) {
    return normalizeNotificationResourceRequestFeedbackStateUpdated(
      notification
    )
  }

  if (notification.type === NotificationType.ResourceRequestCommented) {
    return normalizeNotificationResourceRequestCommented(notification)
  }

  if (notification.type === NotificationType.ResourceRequestUpdated)
    return normalizeNotificationResourceRequestUpdated()

  if (notification.type === NotificationType.AddressUpdated)
    return normalizeNotificationAddressUpdated()

  if (notification.type === NotificationType.LeaveRequestCreated)
    return normalizeNotificationLeaveRequestCreated(notification)

  if (notification.type === NotificationType.LeaveRequestUpdated)
    return normalizeNotificationLeaveRequestUpdated(notification)

  if (notification.type === NotificationType.LeaveRequestAccepted)
    return normalizeNotificationLeaveRequestAccepted(notification)

  if (notification.type === NotificationType.LeaveRequestAcceptedNotice)
    return normalizeNotificationLeaveRequestAcceptedNotice(notification)

  if (notification.type === NotificationType.LeaveRequestPartiallyAccepted)
    return normalizeNotificationLeaveRequestPartiallyAccepted(notification)

  if (notification.type === NotificationType.LeaveRequestDeclined)
    return normalizeNotificationLeaveRequestDeclined(notification)

  if (notification.type === NotificationType.LeaveRequestDeclinedNotice)
    return normalizeNotificationLeaveRequestDeclinedNotice(notification)

  if (notification.type === NotificationType.LeaveRequestCancelRequested)
    return normalizeNotificationLeaveRequestCancelRequested(notification)

  if (notification.type === NotificationType.LeaveRequestCancelAccepted)
    return normalizeNotificationLeaveRequestCancelAccepted(notification)

  if (notification.type === NotificationType.LeaveRequestCancelAcceptedNotice)
    return normalizeNotificationLeaveRequestCancelAcceptedNotice(notification)

  if (
    notification.type === NotificationType.LeaveRequestCancelPartiallyAccepted
  )
    return normalizeNotificationLeaveRequestCancelPartiallyAccepted(
      notification
    )

  if (notification.type === NotificationType.LeaveRequestCancelDeclined)
    return normalizeNotificationLeaveRequestCancelDeclined(notification)

  if (notification.type === NotificationType.LeaveRequestCancelDeclinedNotice)
    return normalizeNotificationLeaveRequestCancelDeclinedNotice(notification)

  if (notification.type === NotificationType.LeaveRequestAborted)
    return normalizeNotificationLeaveRequestAborted(notification)

  if (notification.type === NotificationType.ExternalServiceRequestCreated)
    return normalizeNotificationExternalServiceRequestCreated(notification)

  if (notification.type === NotificationType.ExternalServiceRequestUpdated)
    return normalizeNotificationExternalServiceRequestUpdated(notification)

  if (notification.type === NotificationType.ExternalServiceRequestAccepted)
    return normalizeNotificationExternalServiceRequestAccepted(notification)

  if (
    notification.type === NotificationType.ExternalServiceRequestAcceptedNotice
  )
    return normalizeNotificationExternalServiceRequestAcceptedNotice(
      notification
    )

  if (notification.type === NotificationType.ExternalServiceRequestDeclined)
    return normalizeNotificationExternalServiceRequestDeclined(notification)

  if (
    notification.type === NotificationType.ExternalServiceRequestDeclinedNotice
  )
    return normalizeNotificationExternalServiceRequestDeclinedNotice(
      notification
    )

  throw new Error(
    `notificationNormalized is undefined, maybe normalization for this type (${notification.type}) is missing?`
  )
}

export function normalizeNotificationBase(
  notification: NotificationFromQuery
): NotificationBase {
  const createdDate = parseServerDateString(notification.createdAt)
  if (!createdDate) throw new Error('dateCreated is undefined')

  return {
    id: notification.id,
    date: createdDate,
    isUnread: notification.state === 'unread',
  }
}

/**
 * Computes a map which enables to check if there are any unread notifications for a
 * specific resource request
 */
const unreadNotificationsForResourceRequest = computed(() => {
  if (!notificationsFiltered.value) return

  type ResourceAllocationId = number
  type NotificationId = number

  const unreadNotificationsByResourceAllocationId = new Map<
    ResourceAllocationId,
    NotificationId[]
  >()

  function isAllowedNotificationType(
    type: NotificationType
  ): type is
    | NotificationType.ResourceRequestFeedbackStateUpdated
    | NotificationType.ResourceRequestCommented {
    if (type === NotificationType.ResourceRequestFeedbackStateUpdated)
      return true
    if (type === NotificationType.ResourceRequestCommented) return true

    return false
  }

  notificationsFiltered.value.forEach((notification) => {
    if (notification?.state !== 'unread') return
    if (!isAllowedNotificationType(notification.type)) return
    if (notification.details?.__typename !== 'ResourceRequest') return

    notification.details.resourceAllocationIds?.forEach(
      (resourceAllocationId) => {
        if (isNil(resourceAllocationId)) return

        // Check if needs intial empty array
        const hasNoEntriesYet =
          !unreadNotificationsByResourceAllocationId.has(resourceAllocationId)

        // Maybe create initial empty array
        if (hasNoEntriesYet) {
          unreadNotificationsByResourceAllocationId.set(
            resourceAllocationId,
            []
          )
        }

        // Store ids
        unreadNotificationsByResourceAllocationId
          .get(resourceAllocationId)!
          .push(notification.id)
      }
    )
  })

  return unreadNotificationsByResourceAllocationId
})

function getNotificationForId(id: number) {
  if (!notificationsNormalized.value?.has(id)) return
  return notificationsNormalized.value.get(id)
}

export default {
  notifications: notificationsNormalized,
  isLoading,
  hasUnreadNotifications,
  getNotificationForId,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  unreadNotificationsForResourceRequest,
  getNotifications,
  xstate,
  showRead,
}
